import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import {
  emptyMatchInfo,
  readServerMatchInfo,
  ServerMatchInfo,
} from '../grid/match-info'
import { MatchTargetKey, RowOrigin } from '../models/source-key.model'
import { RowStatus, WorkbenchRow } from '../models/workbench-row.model'

/**
 * A group's identity, given its members: the member ids ordered and joined, so that any two views of
 * the same membership produce the same string.
 */
const keyOfMembers = (members: Set<string>): string =>
  [...members].sort((a, b) => a.localeCompare(b)).join('|')

/**
 * Index over the *server's* match state for every loaded row.
 *
 * Before the write API existed this was an in-memory reconciliation engine that invented its own
 * group ids and mutated `WorkbenchRow.group` in place. It is no longer that: matching is a server
 * operation now (`POST /flex-transactions/actions`, PVAL-26291), so Status, the four match-count
 * columns, related-row tinting and the match-count cross-filter all read the read API's
 * `matchStatus` and per-counterpart match columns (`grid/match-info.ts`) through this index instead of a locally-invented
 * group. {@link init} rebuilds the index from the fetched rows; nothing here mints a group id.
 *
 * `changes$` survives for one purpose: announcing which rows a refresh or an optimistic interim
 * apply touched, so the grids can `refreshCells` / `redrawRows` the right rows.
 *
 * The local-group API (`groupMembers`, `groupsFor`, `otherOriginsFor`, `match`, `unmatchGroups`,
 * `unmatchOrigin`) is retained deliberately: `libs/panels/positions-2` carries a copy of this whole
 * layer under identical names so the eventual `workbench-core` extraction stays mechanical, and
 * {@link ignore} still backs the Prometheus Unignore item, which is local-only because helios !498
 * ships no `unignore` verb (PVAL-26309). The flex path no longer calls any of it, and `match` no
 * longer merges existing groups — merging two groups is not a perseus primitive and the server 400s
 * on it.
 */
@Injectable()
export class MatchingService {
  private readonly rows = new Map<string, WorkbenchRow>()

  /** Which origins a given row id is loaded under. `matchedIds` is flattened across the server's
   * per-source keys, so an id's target is resolved from the loaded row set rather than from the key
   * it arrived under — which also keeps Custody and Trading distinguishable, since both feeds share
   * the one `ext_tran_id` namespace. A set rather than a single origin because the Enfusion and
   * Prometheus id spaces are independent and so could collide. (The sample curl on the ticket
   * appeared to show such a collision, matching id `1316698909` to itself; the backend has since
   * confirmed that was a copy-paste artifact, not a real overlap.) */
  private readonly originsById = new Map<string, Set<RowOrigin>>()

  private readonly index = new Map<string, ServerMatchInfo>()

  /**
   * TEMPORARY SEAM (PVAL-26130 CHANGE 6). The write path is asynchronous — actions endpoint ->
   * command -> RabbitMQ -> portfolio -> `FlexMatchEvent` -> flex-transactions re-hydrates -> UI
   * event — and the POST response is only a command id. Until those UI events are deployed
   * (`FlexTransactionUpdate` / `FlexExternalTransactionUpdate`), the grid applies the expected
   * outcome locally so the user sees something, then refetches to replace it with server truth and
   * reverts if the refetch disagrees.
   *
   * Note that for a brand-new match group the refetch will ALWAYS disagree, by backend design:
   * portfolio applies nothing for a `FLEX_MATCH` in-session, so the rows stay unmatched until the
   * next Run. That is a disclosure, not a failure — see `actionPendingNeedsRun` in the grid.
   *
   * This is NOT the permanent answer. When the event is live, this overlay and the two
   * `applyOptimistic*` methods should be deleted, leaving only the event-driven rows refresh.
   */
  private readonly statusOverrides = new Map<string, RowStatus>()

  private seq = 1
  private readonly changesSubject = new Subject<Set<string>>()
  readonly changes$: Observable<Set<string>> =
    this.changesSubject.asObservable()

  /**
   * Rebuild the index from a freshly fetched row set. Replaces everything; nothing is merged.
   *
   * This also drops the optimistic overlay, which is deliberate: a fetch IS server truth, so an
   * interim guess must not survive it — that is what makes the reconcile check in the grid honest.
   */
  init(rows: WorkbenchRow[]): void {
    this.rows.clear()
    this.originsById.clear()
    this.index.clear()
    this.statusOverrides.clear()
    rows.forEach(row => {
      this.rows.set(row.rowId, row)
      this.index.set(row.rowId, readServerMatchInfo(row))
      const origins = this.originsById.get(row.rowId) ?? new Set<RowOrigin>()
      origins.add(row.origin)
      this.originsById.set(row.rowId, origins)
    })
  }

  // ---- server truth ---------------------------------------------------------

  /**
   * The row's status: an optimistic interim apply first, then the local-only `ignored` flag, then
   * the read API's own `matchStatus` verdict, which `row.adapter.ts` has already normalised onto
   * {@link WorkbenchRow.serverStatus}.
   *
   * The override genuinely has to win, and until PVAL-26309 the code checked `row.ignored` first
   * while this docstring already claimed otherwise. Helios !498 permits hold on an already-ignored
   * row, so with `ignored` first a hold applied to such a row could never be displayed — it would
   * read `ignored` forever. Note the reorder also changes display for a row that is locally ignored
   * and then matched; that is asserted in `matching.service.spec.ts` rather than left incidental.
   *
   * Overrides are applied by {@link applyOptimisticMatch}, {@link applyOptimisticUnmatch} and
   * {@link applyOptimisticStatus}, and dropped wholesale by {@link init}.
   */
  statusOf(row: WorkbenchRow): RowStatus {
    const override = this.statusOverrides.get(row.rowId)
    if (override) return override
    if (row.ignored) return 'ignored'
    return row.serverStatus ?? 'unmatched'
  }

  isMatched(row: WorkbenchRow): boolean {
    return this.statusOf(row) === 'matched'
  }

  /** Ids the server says this row is matched to. */
  matchedIdsOf(row: WorkbenchRow): string[] {
    return this.infoFor(row).matchedIds
  }

  /**
   * A stable identity for the match group this row belongs to, or undefined when it is unmatched.
   *
   * The contract has no group id — group indirection is entirely server-side — so identity is
   * derived from membership: the row plus everything it is matched to, ordered. Two rows in the same
   * group produce the same key, which is what lets the menu reject a selection spanning two groups
   * (the server 400s on merging) without ever seeing a `matchId`.
   *
   * For "how many groups does this selection span?", use {@link matchGroupKeysOf} rather than
   * de-duplicating this over a row set — see the caveat there about a partial `matchedIds` list
   * yielding two keys for one group.
   */
  matchGroupKeyOf(row: WorkbenchRow): string | undefined {
    const members = this.groupMembersOf(row)
    return members ? keyOfMembers(members) : undefined
  }

  /**
   * How many distinct match groups a set of rows spans, as one canonical key per group.
   *
   * Grouping is by connected components over membership, NOT by taking {@link matchGroupKeyOf} of
   * each row and de-duplicating: `matchedIds` is a flat list with no contractual promise that it
   * names every other member, so two rows of the same group can legitimately produce two different
   * keys — an Enfusion row that lists only its Custody counterpart yields `a1|p1` where its sibling
   * yields `a2|p1`. Folding rows whose membership sets intersect into one component fixes that, and
   * cannot over-count: the components this holds are pairwise disjoint, so any two it merges provably
   * share a member and are therefore the same group.
   *
   * It can still UNDER-count, because a member outside the loaded page (`PAGE_SIZE` rows) is invisible from
   * here and cannot link two components. So the result is a floor on how many groups an action would
   * touch — enough to prove "this is more than one group", never enough to promise "exactly N". Copy
   * that shows the number to a user must word it as a minimum.
   *
   * @param rows the rows in question; unmatched rows contribute no group
   * @returns one canonical key per distinct group, each the sorted member ids joined by `|`
   */
  matchGroupKeysOf(rows: readonly WorkbenchRow[]): string[] {
    const components: Set<string>[] = []

    rows.forEach(row => {
      const members = this.groupMembersOf(row)
      if (!members) return

      // Pull every component this row reaches into `members`, then re-seat the union as one
      // component — keeping the invariant that components never overlap.
      components
        .filter(component => [...members].some(id => component.has(id)))
        .forEach(overlapping => {
          overlapping.forEach(id => members.add(id))
          components.splice(components.indexOf(overlapping), 1)
        })
      components.push(members)
    })

    return components.map(keyOfMembers)
  }

  /**
   * Every row id in this row's match group, or undefined when the row is not matched.
   *
   * Membership is the row itself plus everything the server says it is matched to. An optimistic
   * interim apply is honoured through {@link isMatched}, so a row whose apply has just detached it
   * reports no group even while its stale `matchedIds` are still on the row.
   */
  private groupMembersOf(row: WorkbenchRow): Set<string> | undefined {
    if (!this.isMatched(row)) return undefined
    return new Set([row.rowId, ...this.matchedIdsOf(row)])
  }

  /**
   * How many rows of `target` this row is matched to.
   *
   * The server's own count column wins when the row carries it, because it counts matches the
   * client cannot see: the panel holds a single bounded page (`PAGE_SIZE` rows) per source, and a source no
   * grid is pointed at is not loaded at all. Only when the server did not report this target is the
   * number derived from the matched ids resolved against the loaded rows.
   */
  matchCount(row: WorkbenchRow, target: MatchTargetKey): number {
    const info = this.infoFor(row)
    const reported = info.counts[target]
    if (reported != null) return reported
    return this.matchedRowIdsFrom(info, row, target).length
  }

  /**
   * The loaded row ids of `target` this row is matched to — what the count-cell cross-filter
   * narrows the sibling grid to. Only ids actually present in the loaded row set can be resolved to
   * a target, so this is a subset of {@link matchCount} whenever the match reaches beyond the page.
   */
  matchedRowIds(row: WorkbenchRow, target: MatchTargetKey): string[] {
    return this.matchedRowIdsFrom(this.infoFor(row), row, target)
  }

  /**
   * Every row id related to the given rows through a server match — the cross-grid `wb-related`
   * tint. Replaces the old "groups selected in the other grid" set: with server truth there is no
   * group id to compare, and the matched ids say directly which rows to light up.
   *
   * @param rowIds the rows selected in the other grid
   * @returns the ids those rows are matched to, plus the rows themselves
   */
  relatedRowIdsFor(rowIds: string[]): Set<string> {
    const related = new Set<string>()
    rowIds.forEach(rowId => {
      const row = this.rows.get(rowId)
      if (!row || !this.isMatched(row)) return
      related.add(rowId)
      this.matchedIdsOf(row).forEach(id => related.add(id))
    })
    return related
  }

  // ---- optimistic interim (PVAL-26130 CHANGE 6 seam) ------------------------

  /** Snapshot of the whole overlay, so a failed or contradicted apply can be reverted exactly. */
  snapshotMatchState(): MatchStateSnapshot {
    return {
      statusOverrides: new Map(this.statusOverrides),
      index: new Map(
        [...this.index].map(([rowId, info]) => [
          rowId,
          { matchedIds: [...info.matchedIds], counts: { ...info.counts } },
        ]),
      ),
    }
  }

  restoreMatchState(snapshot: MatchStateSnapshot): void {
    const affected = new Set([
      ...this.statusOverrides.keys(),
      ...snapshot.statusOverrides.keys(),
    ])
    this.statusOverrides.clear()
    snapshot.statusOverrides.forEach((status, rowId) =>
      this.statusOverrides.set(rowId, status),
    )
    this.index.clear()
    snapshot.index.forEach((info, rowId) => {
      this.index.set(rowId, info)
      affected.add(rowId)
    })
    this.emit(affected)
  }

  /**
   * Interim apply of a match: every given row becomes matched to every other given row.
   *
   * Counts are recomputed from the resulting membership rather than kept from the server, because a
   * stale server count beside a freshly-matched row is exactly the inconsistency this change set
   * exists to remove.
   *
   * @param rowIds the rows submitted in the action
   */
  applyOptimisticMatch(rowIds: string[]): void {
    const rows = this.resolve(rowIds)
    if (rows.length < 2) return
    const affected = new Set<string>()

    rows.forEach(row => {
      const others = rows
        .filter(other => other.rowId !== row.rowId)
        .map(other => other.rowId)
      const info = this.infoFor(row)
      this.index.set(row.rowId, {
        matchedIds: [...new Set([...info.matchedIds, ...others])],
        // Drop the server's per-target counts: they predate this match, and a derived count over the
        // new membership is at least self-consistent with what the cross-filter will find.
        counts: {},
      })
      this.statusOverrides.set(row.rowId, 'matched')
      affected.add(row.rowId)
    })

    this.emit(affected)
  }

  /**
   * Interim apply of an unmatch: the whole group each given row belongs to is dissolved, matching
   * the server, which resolves the rows to `matchId`s and dissolves each one.
   *
   * @param rowIds the rows submitted in the action
   */
  applyOptimisticUnmatch(rowIds: string[]): void {
    const affected = new Set<string>()

    this.resolve(rowIds).forEach(row => {
      const members = [row.rowId, ...this.matchedIdsOf(row)]
      members.forEach(memberId => {
        this.index.set(memberId, emptyMatchInfo())
        this.statusOverrides.set(memberId, 'unmatched')
        affected.add(memberId)
      })
    })

    this.emit(affected)
  }

  /**
   * Interim apply of a flat status change — the shape `hold` / `ignore` need (PVAL-26309).
   *
   * Deliberately NOT {@link applyOptimisticMatch}: that one early-returns below two rows and writes
   * `matched`, so routing a hold through it would render a multi-row hold as MATCHED and a
   * single-row hold as nothing at all. Match state is untouched here — a hold does not dissolve or
   * form a match group, and !498 writes an `ExtTranStatus`, not a flex match group — so only the
   * status overlay moves.
   *
   * @param rowIds the rows submitted in the action
   * @param status the status to show them as until server truth replaces it
   */
  applyOptimisticStatus(rowIds: string[], status: RowStatus): void {
    const affected = new Set<string>()

    this.resolve(rowIds).forEach(row => {
      this.statusOverrides.set(row.rowId, status)
      affected.add(row.rowId)
    })

    this.emit(affected)
  }

  // ---- local-group API, retained for the shared shape ----------------------

  groupMembers(group: string): WorkbenchRow[] {
    return [...this.rows.values()].filter(row => row.group === group)
  }

  /** Distinct origins other than the given rows' own, within the rows' groups. */
  otherOriginsFor(rowIds: string[]): RowOrigin[] {
    const rows = this.resolve(rowIds).filter(row => row.group)
    const groups = new Set(rows.map(row => row.group))
    const ownOrigins = new Set(rows.map(row => row.origin))
    const others = new Set<RowOrigin>()
    this.rows.forEach(row => {
      if (row.group && groups.has(row.group) && !ownOrigins.has(row.origin)) {
        others.add(row.origin)
      }
    })
    return [...others]
  }

  groupsFor(rowIds: string[]): string[] {
    const groups = this.resolve(rowIds)
      .map(row => row.group)
      .filter((g): g is string => !!g)
    return [...new Set(groups)]
  }

  /**
   * Local-only match, for the non-flex paths and the positions-2 copy.
   *
   * Merging is gone (PVAL-26130 CHANGE 3): a selection that already spans an existing group is
   * refused outright rather than absorbed, because merging two groups is not a perseus primitive
   * and the flex endpoint 400s on it. The flex path does not call this — it posts an action.
   */
  match(rowIds: string[]): void {
    const rows = this.resolve(rowIds)
    if (rows.length < 2) return
    if (rows.some(row => row.group)) return
    const group = `m${this.seq++}`
    const affected = new Set<string>()
    rows.forEach(row => {
      row.group = group
      row.ignored = false
      affected.add(row.rowId)
    })
    this.emit(affected)
  }

  /** Detach the whole group(s) the given rows belong to. */
  unmatchGroups(groups: string[]): void {
    const set = new Set(groups)
    const affected = new Set<string>()
    this.rows.forEach(row => {
      if (row.group && set.has(row.group)) {
        affected.add(row.rowId)
        row.group = undefined
      }
    })
    this.emit(affected)
  }

  /**
   * Detach only the members of a given origin from the group(s), dissolving orphaned groups.
   *
   * Unreachable from the flex path on purpose: partial unmatch has no backend primitive
   * (`RemoveMatchGroupMembersCommand` is explicitly not wired), so the flex unmatch is whole-group
   * only. Retained because positions-2 carries the same method name.
   */
  unmatchOrigin(groups: string[], origin: RowOrigin): void {
    const set = new Set(groups)
    const affected = new Set<string>()
    this.rows.forEach(row => {
      if (row.group && set.has(row.group) && row.origin === origin) {
        affected.add(row.rowId)
        row.group = undefined
      }
    })
    groups.forEach(group => {
      const remaining = this.groupMembers(group)
      if (remaining.length < 2) {
        remaining.forEach(row => {
          affected.add(row.rowId)
          row.group = undefined
        })
      }
    })
    this.emit(affected)
  }

  ignore(rowIds: string[], value: boolean): void {
    const affected = new Set<string>()
    this.resolve(rowIds).forEach(row => {
      if (row.origin !== 'cust' && row.origin !== 'trad') return
      if (value) {
        row.ignored = true
        row.group = undefined
      } else {
        row.ignored = false
      }
      affected.add(row.rowId)
    })
    this.emit(affected)
  }

  // ---- internals ------------------------------------------------------------

  /**
   * The indexed facts for a row, falling back to reading the row's own raw fields when it is not in
   * the index. Value getters run for rows the grid holds directly, which is not always the same set
   * the store last published.
   */
  private infoFor(row: WorkbenchRow): ServerMatchInfo {
    return this.index.get(row.rowId) ?? readServerMatchInfo(row)
  }

  private matchedRowIdsFrom(
    info: ServerMatchInfo,
    row: WorkbenchRow,
    target: MatchTargetKey,
  ): string[] {
    return info.matchedIds.filter(id => {
      // A row is never its own match, even when the two sides' id spaces collide.
      if (id === row.rowId && this.originsById.get(id)?.size === 1) return false
      return this.originsById.get(id)?.has(target) ?? false
    })
  }

  private resolve(rowIds: string[]): WorkbenchRow[] {
    return rowIds
      .map(id => this.rows.get(id))
      .filter((row): row is WorkbenchRow => !!row)
  }

  private emit(affected: Set<string>): void {
    if (affected.size) this.changesSubject.next(affected)
  }
}

/** Opaque snapshot of the optimistic overlay; see {@link MatchingService.snapshotMatchState}. */
export interface MatchStateSnapshot {
  statusOverrides: Map<string, RowStatus>
  index: Map<string, ServerMatchInfo>
}
